import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { FlashcardFlip } from '../components/FlashcardFlip';
import { Button } from '../components/ui/button';
import { ArrowLeft, ArrowRight, RotateCcw, Shuffle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
}

export default function FlashcardReviewPage() {
    const { setId } = useParams();
    const navigate = useNavigate();

    const [flashcardSet, setFlashcardSet] = useState(null);
    const [cards, setCards] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchSet();
    }, [setId]);

    const fetchSet = async () => {
        try {
            const response = await axios.get(`${API}/sets/${setId}`);
            setFlashcardSet(response.data);
            setCards(shuffleArray(response.data.flashcards));
        } catch (err) {
            console.error('Failed to load set:', err);
        } finally {
            setLoading(false);
        }
    };

    const nextCard = useCallback(() => {
        if (currentIndex < cards.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    }, [currentIndex, cards.length]);

    const prevCard = useCallback(() => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    }, [currentIndex]);

    const reshuffleCards = () => {
        setCards(shuffleArray(cards));
        setCurrentIndex(0);
    };

    const restart = () => {
        setCurrentIndex(0);
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowRight' || e.key === 'd') {
                nextCard();
            } else if (e.key === 'ArrowLeft' || e.key === 'a') {
                prevCard();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [nextCard, prevCard]);

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="pt-24 flex items-center justify-center min-h-[80vh]">
                    <div className="animate-pulse text-muted-foreground">Loading...</div>
                </div>
            </div>
        );
    }

    const currentCard = cards[currentIndex];
    const progress = ((currentIndex + 1) / cards.length) * 100;

    return (
        <div className="min-h-screen bg-background">
            <Header />

            {/* Progress Bar */}
            <div className="fixed top-16 left-0 right-0 z-40 bg-background border-b border-border">
                <div className="h-1 bg-muted">
                    <div 
                        className="progress-beam"
                        style={{ width: `${progress}%` }}
                        data-testid="progress-bar"
                    />
                </div>
                <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
                    <Button
                        variant="ghost"
                        onClick={() => navigate(`/study/${setId}`)}
                        data-testid="back-button"
                        className="rounded-none"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Back
                    </Button>
                    <span className="font-mono text-xs tracking-wider text-muted-foreground">
                        {currentIndex + 1} / {cards.length}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={reshuffleCards}
                            data-testid="shuffle-button"
                            className="rounded-none"
                        >
                            <Shuffle className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={restart}
                            data-testid="restart-button"
                            className="rounded-none"
                        >
                            <RotateCcw className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                    </div>
                </div>
            </div>

            <main className="pt-32 pb-16 px-6">
                <div className="max-w-2xl mx-auto">
                    {/* Set Title */}
                    <h1 
                        data-testid="set-title"
                        className="font-serif text-2xl tracking-tight text-center mb-8"
                    >
                        {flashcardSet?.title}
                    </h1>

                    {/* Flashcard */}
                    <div className="flex justify-center mb-8">
                        {currentCard && (
                            <FlashcardFlip
                                key={currentCard.id}
                                front={currentCard.term}
                                back={currentCard.definition}
                            />
                        )}
                    </div>

                    {/* Navigation */}
                    <div className="flex justify-center gap-4">
                        <Button
                            variant="outline"
                            onClick={prevCard}
                            disabled={currentIndex === 0}
                            data-testid="prev-card-button"
                            className="rounded-none px-8"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Previous
                        </Button>
                        <Button
                            onClick={nextCard}
                            disabled={currentIndex === cards.length - 1}
                            data-testid="next-card-button"
                            className="rounded-none px-8"
                        >
                            Next
                            <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                        </Button>
                    </div>

                    {/* Keyboard hint */}
                    <p className="text-center text-xs text-muted-foreground mt-8">
                        Use arrow keys or A/D to navigate • Click card to flip
                    </p>
                </div>
            </main>
        </div>
    );
}
